import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParticipant } from '../../contexts/ParticipantContext';
import { BrCard } from '@govbr-ds/react-components';
import { BrButton } from '@govbr-ds/react-components';

export default function ParticipantInstructionsPage() {
    const navigate = useNavigate();
    const { state } = useParticipant();

    useEffect(() => {
        const timer = setTimeout(() => {
            if (!state.session && !state.isLoading) {
                navigate('/participante');
            }
        }, 100);
        return () => clearTimeout(timer);
    }, [state.session, state.isLoading, navigate]);

    const handleStart = () => {
        navigate('/participante/tarefa/0');
    };

    if (!state.session) {
        return (
            <div className="container-lg py-4">
                <div className="row justify-content-center">
                    <div className="col-12 col-md-10 col-lg-8">
                        <BrCard>
                            <div className="card-content text-center">
                                <div className="animate-pulse">
                                    <div className="h-8 bg-gray-10 rounded w-75 mx-auto mb-4"></div>
                                    <div className="h-4 bg-gray-10 rounded w-50 mx-auto"></div>
                                </div>
                                <p className="text-gray-40 mt-4">Carregando...</p>
                            </div>
                        </BrCard>
                    </div>
                </div>
            </div>
        );
    }

    const personaDescriptions: Record<string, string> = {
        olivia: 'cidadão(ã) que prefere linguagem simples e direta',
        john_smith: 'cidadão(ã) com preferência por textos maiores e alto contraste',
        dr_charles: 'profissional jurídico com familiaridade técnica',
        dra_emily: 'profissional jurídico com preferência por modo escuro'
    };

    return (
        <div className="container-lg py-4">
            <div className="row justify-content-center">
                <div className="col-12 col-md-10 col-lg-8 d-flex flex-column gap-4">
                    {/* Main Card */}
                    <BrCard>
                        <h1 className="text-2xl font-bold text-gray-80 mb-6">
                            Como Funciona a Pesquisa
                        </h1>

                        {/* Personalized greeting - Success message */}
                        <div className="bg-success border-l-4 border-success p-4 rounded-r-lg mb-6" style={{ backgroundColor: 'rgba(22, 136, 33, 0.1)' }}>
                            <div className="d-flex align-items-start gap-3">
                                <span className="text-xl text-success">✓</span>
                                <div>
                                    <p className="font-semibold text-gray-80">Perfil identificado!</p>
                                    <p className="text-sm text-gray-60 mt-1">
                                        Você será tratado(a) como <strong>{personaDescriptions[state.session.determined_persona]}</strong>.
                                        As interfaces serão adaptadas às suas preferências.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Instructions */}
                        <div className="d-flex flex-column gap-6">
                            <section>
                                <h2 className="text-lg font-semibold text-gray-80 mb-3 d-flex align-items-center gap-2">
                                    <span className="w-6 h-6 bg-primary-default text-pure-0 rounded-full d-flex align-items-center justify-content-center text-sm">1</span>
                                    O que você vai fazer
                                </h2>
                                <ul className="list-disc ml-8 d-flex flex-column gap-2 text-gray-60">
                                    <li className="d-flex align-items-start gap-2">
                                        <span className="text-primary-default">•</span>
                                        Você verá <strong className="text-gray-80">{state.session.assigned_tasks.length || 3} telas diferentes</strong> de consulta processual
                                    </li>
                                    <li className="d-flex align-items-start gap-2">
                                        <span className="text-primary-default">•</span>
                                        Para cada tela, um <strong className="text-gray-80">cenário</strong> explicará qual tarefa você deve realizar
                                    </li>
                                    <li className="d-flex align-items-start gap-2">
                                        <span className="text-primary-default">•</span>
                                        Após explorar cada tela, você responderá a uma <strong className="text-gray-80">avaliação curta</strong>
                                    </li>
                                    <li className="d-flex align-items-start gap-2">
                                        <span className="text-primary-default">•</span>
                                        Ao final, você receberá um <strong className="text-gray-80">código de participação</strong>
                                    </li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-lg font-semibold text-gray-80 mb-3 d-flex align-items-center gap-2">
                                    <span className="w-6 h-6 bg-primary-default text-pure-0 rounded-full d-flex align-items-center justify-content-center text-sm">2</span>
                                    Dicas importantes
                                </h2>
                                <ul className="list-disc ml-8 d-flex flex-column gap-2 text-gray-60">
                                    <li className="d-flex align-items-start gap-2">
                                        <span className="text-success">✓</span>
                                        Não há respostas certas ou erradas
                                    </li>
                                    <li className="d-flex align-items-start gap-2">
                                        <span className="text-success">✓</span>
                                        Explore a interface como faria normalmente
                                    </li>
                                    <li className="d-flex align-items-start gap-2">
                                        <span className="text-success">✓</span>
                                        Seja sincero(a) nas suas avaliações
                                    </li>
                                    <li className="d-flex align-items-start gap-2">
                                        <span className="text-success">✓</span>
                                        Você pode desistir a qualquer momento
                                    </li>
                                </ul>
                            </section>

                            {/* Example preview */}
                            <section className="bg-gray-2 p-4 rounded-lg border border-gray-10">
                                <h2 className="text-sm font-semibold text-gray-60 mb-3 uppercase tracking-wide">
                                    Exemplo de tela
                                </h2>
                                <div className="bg-pure-0 border border-gray-10 rounded-lg p-4 shadow-sm">
                                    <div className="d-flex align-items-center gap-2 mb-3 pb-3 border-b border-gray-10">
                                        <span className="text-2xl">📋</span>
                                        <div>
                                            <div className="font-medium text-gray-80">Consulta Processual</div>
                                            <div className="text-sm text-gray-40">Tribunal Superior do Trabalho</div>
                                        </div>
                                    </div>
                                    <div className="d-flex flex-column gap-2 text-sm">
                                        <div className="d-flex justify-content-between">
                                            <span className="text-gray-60">Processo:</span>
                                            <span className="font-mono text-gray-80">RR-XXXXX-XX.XXXX.X.XX.XXXX</span>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span className="text-gray-60">Status:</span>
                                            <span className="text-success font-medium">Em andamento</span>
                                        </div>
                                    </div>
                                </div>
                                <p className="text-xs text-gray-40 mt-2 text-center italic">
                                    Este é apenas um exemplo simplificado
                                </p>
                            </section>
                        </div>

                        {/* Start button */}
                        <BrButton primary
                            onClick={handleStart}
                            className="w-full mt-8 py-4 text-lg bg-success text-pure-0 hover-bg-success"
                        >
                            <span>Iniciar Experimento</span>
                            <span>→</span>
                        </BrButton>

                        <p className="text-center text-xs text-gray-40 mt-4">
                            Ao clicar em "Iniciar", você concorda em participar da pesquisa conforme o termo aceito anteriormente.
                        </p>
                    </BrCard>
                </div>
            </div>
        </div>
    );
}
